import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { PaidGate } from "@/components/PaidGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard,
  ExternalLink,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Clock,
  Zap,
  ArrowUpRight,
  Info,
} from "lucide-react";

const money = (cents) => `$${(cents / 100).toFixed(0).toLocaleString()}`;

function TradelineCard({ tl, onBuy }) {
  const soldOut = (tl.spots_available || 0) <= 0;
  return (
    <div
      className="card-surface p-6 flex flex-col relative overflow-hidden"
      data-testid={`tradeline-card-${tl.id}`}
    >
      {soldOut && (
        <div className="absolute inset-0 bg-[#0B0C0E]/85 z-10 flex items-center justify-center">
          <span className="text-xs px-3 py-1 rounded-full font-mono uppercase tracking-widest bg-[#FF6B4A]/15 text-[#FF6B4A]">
            Sold out
          </span>
        </div>
      )}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline mb-1.5">{tl.account_type?.replace("_", " ")}</div>
          <div className="font-display text-2xl leading-tight">{tl.bank_name}</div>
        </div>
        <div className="text-right">
          <div className="ticker text-3xl text-[#D5FF3F]">{money(tl.price_cents)}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
            One-time
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-[#26282C]">
        <div>
          <div className="overline mb-1">Limit</div>
          <div className="ticker text-lg text-[#F5F3EE]">${tl.credit_limit?.toLocaleString()}</div>
        </div>
        <div>
          <div className="overline mb-1">Age</div>
          <div className="ticker text-lg text-[#F5F3EE]">
            {Math.floor((tl.age_months || 0) / 12)}y {(tl.age_months || 0) % 12}m
          </div>
        </div>
        {tl.utilization_pct != null && (
          <div>
            <div className="overline mb-1">Util</div>
            <div className="ticker text-lg text-[#F5F3EE]">{tl.utilization_pct}%</div>
          </div>
        )}
        {tl.statement_date && (
          <div>
            <div className="overline mb-1">Statement</div>
            <div className="ticker text-lg text-[#F5F3EE]">{tl.statement_date}th</div>
          </div>
        )}
      </div>

      {tl.est_score_lift && (
        <div className="flex items-center gap-2 text-sm text-[#7CFF9B] mb-2">
          <TrendingUp size={14} strokeWidth={2} />
          <span className="font-mono">Est. lift · {tl.est_score_lift}</span>
        </div>
      )}
      {tl.posting_speed_days && (
        <div className="flex items-center gap-2 text-sm text-[#A8ADA5] mb-4">
          <Clock size={14} strokeWidth={2} />
          <span className="font-mono">Posts in {tl.posting_speed_days}</span>
        </div>
      )}
      {tl.notes && (
        <p className="text-sm text-[#A8ADA5] mb-5 leading-relaxed line-clamp-3">{tl.notes}</p>
      )}

      <div className="flex items-center justify-between mt-auto pt-2">
        <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C]">
          {tl.spots_available} {tl.spots_available === 1 ? "spot" : "spots"} left
        </div>
        <Button
          onClick={() => onBuy(tl)}
          disabled={soldOut}
          data-testid={`buy-tradeline-btn-${tl.id}`}
          className="rounded-full btn-lime h-10 px-5"
        >
          Add to my report <ArrowUpRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

function CheckoutDialog({ tradeline, open, onClose }) {
  const [form, setForm] = useState({
    full_name: "",
    ssn_last_4: "",
    date_of_birth: "",
    mailing_address_line1: "",
    mailing_address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!tradeline) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/tradelines/checkout", {
        tradeline_id: tradeline.id,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-[#131518] border-[#26282C] text-[#F5F3EE]"
        data-testid="tradeline-checkout-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">
            {tradeline?.bank_name}
          </DialogTitle>
          <DialogDescription className="text-[#A8ADA5]">
            We add you as an <span className="text-[#F5F3EE]">Authorized User</span> — the account&apos;s
            age, limit, and payment history become yours to inherit on your report.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label className="overline">Full legal name</Label>
            <Input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="tl-checkout-full-name"
              className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              placeholder="First Middle Last"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Date of birth</Label>
              <Input
                required
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                data-testid="tl-checkout-dob"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Last 4 of SSN</Label>
              <Input
                required
                maxLength={4}
                minLength={4}
                pattern="\d{4}"
                value={form.ssn_last_4}
                onChange={(e) => setForm({ ...form, ssn_last_4: e.target.value.replace(/\D/g, "") })}
                data-testid="tl-checkout-ssn"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                placeholder="1234"
              />
            </div>
          </div>
          <div>
            <Label className="overline">Mailing address</Label>
            <Input
              required
              value={form.mailing_address_line1}
              onChange={(e) => setForm({ ...form, mailing_address_line1: e.target.value })}
              data-testid="tl-checkout-addr1"
              className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              placeholder="123 Main St"
            />
            <Input
              value={form.mailing_address_line2}
              onChange={(e) => setForm({ ...form, mailing_address_line2: e.target.value })}
              data-testid="tl-checkout-addr2"
              className="mt-2 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              placeholder="Apt / Suite (optional)"
            />
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <Label className="overline">City</Label>
              <Input
                required
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="tl-checkout-city"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div className="col-span-1">
              <Label className="overline">State</Label>
              <Input
                required
                maxLength={2}
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                data-testid="tl-checkout-state"
                className="mt-1 rounded-sm font-mono uppercase bg-[#0B0C0E] border-[#26282C]"
                placeholder="CA"
              />
            </div>
            <div className="col-span-2">
              <Label className="overline">ZIP</Label>
              <Input
                required
                value={form.zip_code}
                onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                data-testid="tl-checkout-zip"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
          </div>
          <div>
            <Label className="overline">Phone</Label>
            <Input
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="tl-checkout-phone"
              className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              placeholder="(555) 555-5555"
            />
          </div>
          <div>
            <Label className="overline">Notes (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="tl-checkout-notes"
              className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]"
              placeholder="Anything we should know?"
              rows={2}
            />
          </div>
          <div className="rounded-sm border border-[#D5FF3F]/20 bg-[#D5FF3F]/5 p-3 flex items-start gap-2 text-xs text-[#A8ADA5] font-mono">
            <ShieldCheck size={13} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
            <span>
              Encrypted at rest. Never used for anything except adding you as an Authorized User on
              this specific tradeline. Refunded within 30 days if it doesn&apos;t post.
            </span>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-full btn-outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="tl-checkout-submit"
              className="rounded-full btn-lime h-11 px-6"
            >
              {submitting ? (
                <>
                  <Sparkles size={15} className="mr-2 animate-pulse" /> Redirecting…
                </>
              ) : (
                <>
                  Pay {tradeline ? money(tradeline.price_cents) : ""}
                  <ArrowUpRight size={15} className="ml-1.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LeadForm() {
  const [form, setForm] = useState({
    goal: "",
    target_score: "",
    budget_cents: "",
    contact_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/tradelines/lead", {
        goal: form.goal,
        target_score: form.target_score ? parseInt(form.target_score, 10) : null,
        budget_cents: form.budget_cents ? Math.round(parseFloat(form.budget_cents) * 100) : null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
      });
      setDone(true);
      toast.success("Got it. We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="card-surface p-8 text-center" data-testid="lead-form-success">
        <Sparkles size={28} className="text-[#D5FF3F] mx-auto mb-4" />
        <div className="font-display text-3xl mb-2">Request received.</div>
        <p className="text-[#A8ADA5] max-w-md mx-auto">
          A concierge will reply within 24 hours with a shortlist matched to your goal and budget.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-surface p-8 space-y-4" data-testid="lead-form">
      <div>
        <div className="overline mb-2">Concierge match</div>
        <h3 className="font-display text-3xl leading-tight">
          Don&apos;t see what you need? <span className="text-[#D5FF3F]">Tell us your goal.</span>
        </h3>
        <p className="text-sm text-[#A8ADA5] mt-2 max-w-lg">
          A specialist will hand-pick tradelines to close your gap — mortgage, auto, or unsecured lines.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Your goal</Label>
          <Input
            required
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            data-testid="lead-goal"
            className="mt-1 rounded-sm font-mono"
            placeholder="e.g. Mortgage in 6 months"
          />
        </div>
        <div>
          <Label className="overline">Target score</Label>
          <Input
            type="number"
            value={form.target_score}
            onChange={(e) => setForm({ ...form, target_score: e.target.value })}
            data-testid="lead-target-score"
            className="mt-1 rounded-sm font-mono"
            placeholder="720"
          />
        </div>
        <div>
          <Label className="overline">Budget ($)</Label>
          <Input
            type="number"
            value={form.budget_cents}
            onChange={(e) => setForm({ ...form, budget_cents: e.target.value })}
            data-testid="lead-budget"
            className="mt-1 rounded-sm font-mono"
            placeholder="500"
          />
        </div>
        <div>
          <Label className="overline">Phone (optional)</Label>
          <Input
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            data-testid="lead-phone"
            className="mt-1 rounded-sm font-mono"
            placeholder="(555) 555-5555"
          />
        </div>
      </div>
      <div>
        <Label className="overline">Anything else?</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="lead-notes"
          className="mt-1 rounded-sm"
          rows={3}
          placeholder="Timeline pressure, current utilization, etc."
        />
      </div>
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="lead-submit"
          className="rounded-full btn-lime h-11 px-6"
        >
          {submitting ? "Sending…" : (<>Request match <ArrowUpRight size={15} className="ml-1.5" /></>)}
        </Button>
      </div>
    </form>
  );
}

export default function TradeLines() {
  const { user } = useAuth();
  const paid = user?.plan === "basic" || user?.plan === "plus";

  const [items, setItems] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // tradeline object

  useEffect(() => {
    if (!paid) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [tl, aff, ord] = await Promise.all([
          api.get("/tradelines"),
          api.get("/tradelines/affiliates"),
          api.get("/tradelines/orders"),
        ]);
        setItems(tl.data || []);
        setAffiliates(aff.data?.affiliates || []);
        setOrders(ord.data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [paid]);

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">New · Tradelines</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Rent history.
          <span className="text-[#D5FF3F]"> Buy score.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Piggyback on aged, low-utilization credit accounts. Get added as an Authorized User and
          inherit their payment history — one of the fastest legit ways to lift your FICO.
        </p>
      </div>

      {!paid ? (
        <PaidGate
          toolName="The tradelines marketplace"
          tagline="Aura Basic and Plus subscribers get access to hand-picked AU tradelines, concierge matching, and vetted third-party providers."
        />
      ) : (
        <>
          {/* Educational strip */}
          <div className="mb-10 grid md:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, title: "Fast lift", body: "30-100 pt jumps are common when a seasoned line posts." },
              { icon: ShieldCheck, title: "100% legal", body: "Authorized User status is protected under ECOA." },
              { icon: Clock, title: "7-30 days", body: "Most lines post to your report within one billing cycle." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="card-surface p-5 flex gap-3">
                  <Icon size={18} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">{f.title}</div>
                    <div className="text-xs text-[#A8ADA5] mt-1 leading-relaxed">{f.body}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Marketplace */}
          <div className="mb-6 flex items-end justify-between">
            <div>
              <div className="overline mb-2">Curated inventory</div>
              <h2 className="font-display text-3xl">Available now.</h2>
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C]">
              {items.length} {items.length === 1 ? "line" : "lines"} live
            </div>
          </div>

          {loading ? (
            <div className="text-[#A8ADA5] mb-12">Loading tradelines…</div>
          ) : items.length === 0 ? (
            <div className="card-surface p-8 mb-12 text-center" data-testid="tradelines-empty">
              <Info size={22} className="text-[#A8ADA5] mx-auto mb-3" />
              <div className="text-[#F5F3EE] mb-1">No live inventory right now.</div>
              <div className="text-sm text-[#A8ADA5]">
                Submit a concierge match below and we&apos;ll source one for you.
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12" data-testid="tradelines-grid">
              {items.map((tl) => (
                <TradelineCard key={tl.id} tl={tl} onBuy={setBuying} />
              ))}
            </div>
          )}

          {/* Your orders */}
          {orders.length > 0 && (
            <div className="mb-12" data-testid="my-tradeline-orders">
              <div className="mb-4">
                <div className="overline mb-2">Your orders</div>
                <h2 className="font-display text-3xl">In the pipeline.</h2>
              </div>
              <div className="card-surface overflow-hidden">
                <div className="divide-y divide-[#26282C]">
                  {orders.map((o) => (
                    <div key={o.id} className="p-5 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <CreditCard size={16} className="text-[#D5FF3F]" />
                        <div>
                          <div className="font-medium">{o.tradeline_snapshot?.bank_name}</div>
                          <div className="text-xs text-[#6D716C] font-mono">
                            {money(o.tradeline_snapshot?.price_cents || 0)} ·
                            {" "}{new Date(o.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#17191C] text-[#A8ADA5]">
                          Pay · {o.payment_status}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${
                          o.fulfillment_status === "posted" || o.fulfillment_status === "delivered"
                            ? "bg-[#D5FF3F] text-[#0B0C0E]"
                            : o.fulfillment_status === "refunded" || o.fulfillment_status === "cancelled"
                            ? "bg-[#FF6B4A]/15 text-[#FF6B4A]"
                            : "bg-[#7CFF9B]/15 text-[#7CFF9B]"
                        }`}>
                          {o.fulfillment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Lead form */}
          <div className="mb-12">
            <LeadForm />
          </div>

          {/* Affiliates */}
          {affiliates.length > 0 && (
            <div className="mb-12" data-testid="affiliates-section">
              <div className="mb-4">
                <div className="overline mb-2">Third-party providers</div>
                <h2 className="font-display text-3xl">Or shop direct.</h2>
                <p className="text-sm text-[#A8ADA5] mt-2 max-w-xl">
                  Vetted partners with their own catalogs. We may earn a referral if you buy through
                  these links — same price, transparent disclosure.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {affiliates.map((a) => (
                  <a
                    key={a.name}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`affiliate-link-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="card-surface p-5 flex items-center justify-between group hover:border-[#D5FF3F]/40 transition-colors"
                  >
                    <div>
                      <div className="font-medium mb-1">{a.name}</div>
                      <div className="text-xs text-[#A8ADA5]">{a.tagline}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-[#D5FF3F]">from {a.starting_price}</div>
                      <ExternalLink size={15} className="text-[#6D716C] group-hover:text-[#D5FF3F]" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="card-surface p-6 border-[#26282C]">
            <div className="flex items-start gap-3">
              <Zap size={16} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#6D716C] leading-relaxed">
                <span className="text-[#A8ADA5]">Disclosure:</span> Authorized-user tradelines are legal
                but their effect on your credit varies by scoring model and lender. Aura Credit does
                not guarantee a specific score change. Refunds are issued if a purchased tradeline does
                not post within 45 days. Tradelines are educational products; you can build the same
                effect for free by asking a family member to add you as an AU.
              </div>
            </div>
          </div>
        </>
      )}

      <CheckoutDialog tradeline={buying} open={!!buying} onClose={() => setBuying(null)} />
    </AppShell>
  );
}
